import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatBytes, formatDateTime } from "../../lib/format";

export default function AdminInstallers() {
    const [installers, setInstallers] = useState([]);
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ product_id: "", os_name: "Windows", file_format: "Full package", version: "1.0.0", release_notes: "", file: null });
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        const [a, b] = await Promise.all([
            api.get("/admin/installers"),
            api.get("/admin/products"),
        ]);
        setInstallers(a.data || []);
        setProducts(b.data || []);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.file) { toast.error("Select a file"); return; }
        if (!form.product_id) { toast.error("Select a product"); return; }
        const fd = new FormData();
        fd.append("product_id", form.product_id);
        fd.append("os_name", form.os_name);
        fd.append("file_format", form.file_format);
        fd.append("version", form.version);
        fd.append("release_notes", form.release_notes);
        fd.append("file", form.file);
        setUploading(true);
        try {
            await api.post("/admin/installers", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Installer uploaded successfully.");
            setOpen(false);
            setForm({ product_id: "", os_name: "Windows", file_format: "Full package", version: "1.0.0", release_notes: "", file: null });
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setUploading(false);
        }
    };

    const setStatus = async (inst, action) => {
        try {
            await api.patch(`/admin/installers/${inst.id}/${action}`);
            toast.success("Updated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (inst) => {
        if (!window.confirm("Delete installer?")) return;
        try {
            await api.delete(`/admin/installers/${inst.id}`);
            toast.success("Deleted");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-installers-page">
                <div>
                    <div className="label-mono mb-3">/ Installers</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Installers</h1>
                    <p className="text-avx-muted mt-2">Manage each plugin's downloadable files.</p>
                </div>
                <button onClick={() => setOpen(true)} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="upload-installer-btn">
                    + Upload installer
                </button>
            </div>

            <div className="panel overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-avx-surface/80 border-b border-avx-line">
                        <tr className="label-mono text-left">
                            <th className="px-4 py-3 font-normal">Product</th>
                            <th className="px-4 py-3 font-normal">System</th>
                            <th className="px-4 py-3 font-normal">Format</th>
                            <th className="px-4 py-3 font-normal">Version</th>
                            <th className="px-4 py-3 font-normal">File</th>
                            <th className="px-4 py-3 font-normal">Size</th>
                            <th className="px-4 py-3 font-normal">Status</th>
                            <th className="px-4 py-3 font-normal">Uploaded</th>
                            <th className="px-4 py-3 font-normal text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {installers.map((inst) => (
                            <tr key={inst.id} className="border-b border-avx-line/60 hover:bg-avx-surface/40" data-testid={`installer-row-${inst.id}`}>
                                <td className="px-4 py-3">{inst.product_name}</td>
                                <td className="px-4 py-3 font-mono">{inst.os}</td>
                                <td className="px-4 py-3 font-mono text-xs">{inst.format}</td>
                                <td className="px-4 py-3 font-mono">{inst.version}</td>
                                <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">{inst.file_name}</td>
                                <td className="px-4 py-3 font-mono text-xs">{formatBytes(inst.file_size)}</td>
                                <td className="px-4 py-3"><StatusBadge status={inst.status} /></td>
                                <td className="px-4 py-3 font-mono text-xs">{formatDateTime(inst.created_at)}</td>
                                <td className="px-4 py-3 text-right space-x-2">
                                    {inst.status !== "active" ? (
                                        <button onClick={() => setStatus(inst, "enable")} className="text-xs font-mono uppercase text-avx-success hover:underline">Activar</button>
                                    ) : (
                                        <button onClick={() => setStatus(inst, "disable")} className="text-xs font-mono uppercase text-avx-muted hover:text-avx-text">Disable</button>
                                    )}
                                    <button onClick={() => remove(inst)} className="text-xs font-mono uppercase text-avx-danger hover:underline">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {installers.length === 0 && <div className="p-8 text-center text-avx-muted text-sm">No installers yet.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-lg">
                    <DialogHeader><DialogTitle>Upload installer</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="installer-form">
                        <div>
                            <Label className="label-mono">Product</Label>
                            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="installer-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                                <SelectContent>
                                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">System</Label>
                                <Select value={form.os_name} onValueChange={(v) => setForm({ ...form, os_name: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Windows">Windows</SelectItem>
                                        <SelectItem value="macOS Intel">macOS · Intel (x86_64)</SelectItem>
                                        <SelectItem value="macOS Apple Silicon">macOS · Apple Silicon (arm64)</SelectItem>
                                        <SelectItem value="macOS">macOS · Universal (Intel + Silicon)</SelectItem>
                                        <SelectItem value="Manual">Manual / PDF</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Format</Label>
                                <Select value={form.file_format} onValueChange={(v) => setForm({ ...form, file_format: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="VST3">VST3</SelectItem>
                                        <SelectItem value="AU">AU</SelectItem>
                                        <SelectItem value="Full package">Full package</SelectItem>
                                        <SelectItem value="Manual">Manual</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="label-mono">Version</Label>
                            <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} required data-testid="installer-version" />
                        </div>
                        <div>
                            <Label className="label-mono">Release notes</Label>
                            <Textarea rows={3} className="mt-2 bg-avx-bg border-avx-line" value={form.release_notes} onChange={(e) => setForm({ ...form, release_notes: e.target.value })} />
                        </div>
                        <div>
                            <Label className="label-mono">File (.exe, .msi, .dmg, .pkg, .zip, .pdf)</Label>
                            <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                                accept=".exe,.msi,.dmg,.pkg,.zip,.pdf"
                                data-testid="installer-file"
                                className="mt-2 w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-[0.18em] file:bg-avx-blue file:text-avx-bg hover:file:bg-avx-blue/90 cursor-pointer text-avx-muted" />
                        </div>
                        <button type="submit" disabled={uploading} className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="installer-submit-btn">
                            {uploading ? "Uploading…" : "Upload installer"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
